import React, { useCallback, useEffect, useMemo, useState } from "react";
import KaboodleModal from "./KaboodleModal";
import { useNavigate } from "react-router-dom";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { hasApiError } from "utils/error-utils";
import Button from "components/Button/Button";
import Card from "components/Card/Card";
import type { OptionType } from "components/inputs";
import { Select, TextInput } from "components/inputs";
import { useCreateClaimMutation } from "services/claims-api";
import { useGetRresQuery } from "services/rre-api";
import { GLOBAL_SEARCH_PARAM } from "utils/useUrlStore";
import { useQuery } from "utils/hooks";
import "./CreateClaimModal.scss";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type FormData = {
  rre: OptionType;
  icn: string;
};

const CreateClaimModal: React.FC<ModalProps> = ({ isOpen, onClose }) => {
  const [nonFieldErrors, setNonFieldErrors] = useState<string[]>([]);
  const navigate = useNavigate();
  const query = useQuery();

  const { data: rres } = useGetRresQuery(
    { searchTerm: query.get(GLOBAL_SEARCH_PARAM) },
    { skip: false },
  );

  const rresOptions = useMemo(() => {
    return rres?.results.map((rre) => ({
      label: `${rre.rre_id} - ${rre.name}`,
      value: rre.id,
    }));
  }, [rres]);

  const {
    control,
    reset,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      icn: "",
      rre: {},
    },
  });

  const [createClaim, { error, isSuccess }] = useCreateClaimMutation();

  useEffect(() => {
    if (hasApiError(error)) {
      Object.entries(error.data).forEach(([field, messages]) => {
        setError(field as keyof FormData, {
          type: "manual",
          message: messages?.[0] || messages,
        });
      });
    }
  }, [error, setError]);

  useEffect(() => {
    if (isSuccess) {
      onClose();
    }
  }, [onClose, isSuccess]);

  const onSubmit: SubmitHandler<FormData> = async (data: FormData) => {
    const passedData = { ...data, rre: data.rre.value.toString() };
    try {
      const result = await createClaim({
        data: passedData,
      }).unwrap();

      if (result.id) {
        reset();
        onClose();
        navigate(`/section-111/claims/${result.id}/overview`);
      }
    } catch (err) {
      setNonFieldErrors(err.data?.non_field_errors);
      console.error("Failed to create the claim: ", err);
    }
  };

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const footerContent = useMemo(() => {
    return <Button onClick={handleSubmit(onSubmit)}>Save</Button>;
  }, [handleSubmit, onSubmit]);

  return (
    <KaboodleModal isOpen={isOpen} onClose={handleClose} footer={footerContent}>
      <Card heading="Create Claim">
        {!isSuccess && (
          <div className="flex flex-col non-field-errors">
            <ul className="flex flex-col">
              {nonFieldErrors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </div>
        )}
        <Controller
          control={control}
          name="rre"
          rules={{ required: "RRE ID is required" }}
          render={({ field: { name, onChange: onControllerChange } }) => (
            <Select
              name={name}
              placeholder="Enter RRE ID"
              label={"RRE ID"}
              options={rresOptions}
              onChange={(option) => {
                onControllerChange(option);
                clearErrors();
              }}
              errors={errors}
            />
          )}
        />
        <Controller
          control={control}
          name="icn"
          defaultValue=""
          rules={{ required: "ICN is required" }}
          render={({ field }) => (
            <TextInput
              {...field}
              placeholder="ICN"
              label="ICN"
              errors={errors}
              onBlur={() => {
                clearErrors();
              }}
            />
          )}
        />
      </Card>
    </KaboodleModal>
  );
};

export default CreateClaimModal;
